export default class ReviewSlider {
    static init(): void {
        const tracks: NodeListOf<HTMLElement> = document.querySelectorAll('.js-review-slider');
        const desktop = window.matchMedia('(min-width: 1024px)');

        tracks.forEach((track) => {
            let wrap: HTMLElement | null = track.closest('.review-slider');
            const dotsWrap: HTMLElement | null = wrap?.parentElement?.querySelector('.js-review-slider-dots') ?? null;

            if (!wrap) {
                return;
            }

            // untouched markup (all reviews, no clones or listeners) to rebuild from
            const pristine = wrap.cloneNode(true) as HTMLElement;

            // reviews marked data-review-desktop-only are left out below lg
            const build = (): void => {
                const fresh = pristine.cloneNode(true) as HTMLElement;

                if (!desktop.matches) {
                    fresh.querySelectorAll('[data-review-desktop-only]').forEach((item) => item.remove());
                }

                wrap?.replaceWith(fresh);
                wrap = fresh;

                if (dotsWrap) {
                    dotsWrap.innerHTML = '';
                }

                const freshTrack: HTMLElement | null = fresh.querySelector('.js-review-slider');

                if (freshTrack) {
                    this.sliderInit(freshTrack);
                }
            };

            build();
            desktop.addEventListener('change', build);
        });
    }

    static sliderInit(track: HTMLElement): void {
        const viewport: HTMLElement | null = track.closest('.review-slider__viewport');
        const wrap: HTMLElement | null = track.closest('.review-slider');
        const originalItems: HTMLElement[] = Array.from(track.querySelectorAll('.review-slider-item'));

        if (!viewport || !wrap || originalItems.length === 0) {
            return;
        }

        const count = originalItems.length;

        // Loop is simulated by tripling the items (before / original / after) and
        // silently snapping the track back into the middle band once the animation
        // finishes, instead of relying on a slider library's internal clone-index math.
        const beforeItems: HTMLElement[] = [];
        const afterItems: HTMLElement[] = [];
        const beforeFragment = document.createDocumentFragment();
        const afterFragment = document.createDocumentFragment();

        originalItems.forEach((item) => {
            const cloneBefore = item.cloneNode(true) as HTMLElement;
            cloneBefore.setAttribute('aria-hidden', 'true');
            beforeItems.push(cloneBefore);
            beforeFragment.appendChild(cloneBefore);

            const cloneAfter = item.cloneNode(true) as HTMLElement;
            cloneAfter.setAttribute('aria-hidden', 'true');
            afterItems.push(cloneAfter);
            afterFragment.appendChild(cloneAfter);
        });

        track.insertBefore(beforeFragment, track.firstChild);
        track.appendChild(afterFragment);

        const allItems: HTMLElement[] = [...beforeItems, ...originalItems, ...afterItems];

        let renderIndex = count;

        const normalize = (i: number): number => ((i % count) + count) % count;

        const dotsWrap: HTMLElement | null = wrap.parentElement?.querySelector('.js-review-slider-dots') ?? null;
        // translated label comes from the template, "%d" is replaced by the review number
        const dotLabel = dotsWrap?.dataset.dotLabel ?? '%d';
        const dots: HTMLButtonElement[] = originalItems.map((_, dotIndex) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'review-slider__dot';
            dot.setAttribute('aria-label', dotLabel.replace('%d', String(dotIndex + 1)));
            dotsWrap?.appendChild(dot);

            return dot;
        });

        const setActive = (index: number): void => {
            allItems.forEach((item, itemIndex) => {
                item.classList.toggle('review-slider-item--active', itemIndex === index);
            });

            dots.forEach((dot, dotIndex) => {
                const isActive = dotIndex === normalize(index);
                dot.classList.toggle('review-slider__dot--active', isActive);
                dot.setAttribute('aria-current', isActive ? 'true' : 'false');
            });
        };

        const centerOn = (index: number, animate: boolean): void => {
            const item = allItems[index];

            if (!item) {
                return;
            }

            const offset = item.offsetLeft - (viewport.clientWidth - item.offsetWidth) / 2;

            track.style.transitionDuration = animate ? '' : '0s';
            track.style.transform = `translateX(${-offset}px)`;

            if (!animate) {
                // force reflow so the next animated move doesn't pick up the disabled duration
                void track.offsetHeight;
                track.style.transitionDuration = '';
            }
        };

        const goTo = (direction: number): void => {
            renderIndex += direction;
            setActive(renderIndex);
            centerOn(renderIndex, true);
        };

        dots.forEach((dot, dotIndex) => {
            dot.addEventListener('click', () => {
                // take the shorter way around the loop
                let delta = dotIndex - normalize(renderIndex);

                if (delta > count / 2) {
                    delta -= count;
                } else if (delta < -count / 2) {
                    delta += count;
                }

                if (delta !== 0) {
                    goTo(delta);
                }
            });
        });

        track.addEventListener('transitionend', (event: TransitionEvent) => {
            if (event.target !== track || event.propertyName !== 'transform') {
                return;
            }

            if (renderIndex < count || renderIndex >= count * 2) {
                renderIndex = count + normalize(renderIndex);
                setActive(renderIndex);
                centerOn(renderIndex, false);
            }
        });

        const prevButton: HTMLElement | null = wrap.querySelector('.review-slider__arrow--prev');
        const nextButton: HTMLElement | null = wrap.querySelector('.review-slider__arrow--next');

        prevButton?.addEventListener('click', () => goTo(-1));
        nextButton?.addEventListener('click', () => goTo(1));

        let pointerDown = false;
        let pointerStartX = 0;

        viewport.addEventListener('pointerdown', (event: PointerEvent) => {
            pointerDown = true;
            pointerStartX = event.clientX;
            viewport.setPointerCapture(event.pointerId);
        });

        viewport.addEventListener('pointerup', (event: PointerEvent) => {
            if (!pointerDown) {
                return;
            }

            pointerDown = false;

            const delta = event.clientX - pointerStartX;
            const threshold = 40;

            if (delta > threshold) {
                goTo(-1);
            } else if (delta < -threshold) {
                goTo(1);
            }
        });

        viewport.addEventListener('pointercancel', () => {
            pointerDown = false;
        });

        const recenter = (): void => centerOn(renderIndex, false);

        setActive(renderIndex);
        recenter();

        // The offset is measured from the current layout, so re-measure whenever it
        // settles or changes (late CSS/fonts, scrollbar appearing, breakpoints),
        // not only on window resize - otherwise the active card ends up off-center.
        new ResizeObserver(recenter).observe(viewport);

        window.addEventListener('load', recenter);
        window.addEventListener('pageshow', recenter);
        document.fonts?.ready.then(recenter);
    }
}
